// src/contexts/WishlistContext.tsx
//
// Wishlist/favorites state — product-level (not per-variation: color/size get
// chosen when the shopper moves an item to the cart from its product page).
// Persisted to localStorage with no expiry, mirroring how the cart works;
// there is no server-side copy, matching the no-database architecture.

import React, { createContext, ReactNode } from "react";
import { useLocalStorage } from "./useLocalStorage";

export interface WishlistEntry {
  /** Square catalog ITEM id. */
  productId: string;
  addedAt: number;
}

interface WishlistContextType {
  wishlist: WishlistEntry[];
  isInWishlist: (productId?: string | null) => boolean;
  toggleWishlist: (productId?: string | null) => void;
  removeFromWishlist: (productId?: string | null) => void;
  resetWishlist: () => void;
}

const WishlistContext = createContext<WishlistContextType | undefined>(
  undefined,
);

export const WishlistProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [wishlist, setWishlist] = useLocalStorage<WishlistEntry[]>(
    "americana-company-wishlist",
    [],
  );

  const isInWishlist = (productId?: string | null) =>
    !!productId && wishlist.some((entry) => entry.productId === productId);

  const toggleWishlist = (productId?: string | null) => {
    if (!productId) return;
    setWishlist((prev) =>
      prev.some((entry) => entry.productId === productId)
        ? prev.filter((entry) => entry.productId !== productId)
        : [...prev, { productId, addedAt: Date.now() }],
    );
  };

  const removeFromWishlist = (productId?: string | null) => {
    if (!productId) return;
    setWishlist((prev) =>
      prev.filter((entry) => entry.productId !== productId),
    );
  };

  const resetWishlist = () => setWishlist([]);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        isInWishlist,
        toggleWishlist,
        removeFromWishlist,
        resetWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = React.useContext(WishlistContext);
  if (context === undefined) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
